import { Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert, { type AlertAction } from '/@/components/bayer/alert';
import {
    useAcceptAdvisorRvInvitation,
    useAdvisorRvInvitations,
    useAdvisorRvLinks,
    useDeclineAdvisorRvInvitation,
    useDistributorsAndAdvisors,
} from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY } from '.';
import RVOnboardingModalAdvisorSelector from './advisor-selector';
import RVOnboardingModalInitial from './initial';
import RVOnboardingModalNewInvitationsReceived from './new-invitations-received';
import RVOnboardingModalInvites from './pending-invites';

const RVOnboardingModalAndAlert = () => {
    // Check open modal + Alert to show
    const [open, setOpen] = useState(false);
    const advisorRvLinksQuery = useAdvisorRvLinks();
    const advisorsRvInvitationsQuery = useAdvisorRvInvitations();
    const pendingInvitationsQuery = useAdvisorRvInvitations({ status: 'pending', direction: 'received' });

    const hasLinkedAdvisors = (advisorRvLinksQuery.data?.results?.length ?? 0) > 0;
    const hasInvitedAdvisors = (advisorsRvInvitationsQuery.data?.results?.length ?? 0) > 0;
    const hasLinkedOrInvitedAdvisors = hasLinkedAdvisors || hasInvitedAdvisors;
    const wasOpened = localStorage.getItem(ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY) === 'true';

    useEffect(() => {
        if (advisorRvLinksQuery.isLoading || advisorsRvInvitationsQuery.isLoading) return;

        if (!hasLinkedOrInvitedAdvisors && !wasOpened) {
            setOpen(true);
            localStorage.setItem(ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY, 'true');
        }
    }, [wasOpened, advisorRvLinksQuery.isLoading, advisorsRvInvitationsQuery.isLoading, hasLinkedOrInvitedAdvisors]);

    useEffect(() => {
        if (wasOpened && (pendingInvitationsQuery?.data?.count || 0) > 0) {
            setStep('new-invitations-received');
            setOpen(true);
        }
    }, [wasOpened, pendingInvitationsQuery?.data?.count]);

    // Steps
    const [step, setStep] = useState<
        'initial' | 'advisor-selector' | 'pending-invites' | 'new-invitations-received' | 'sent-invites'
    >('initial');

    // Alert title and buttons dynamically depending on user situation
    const acceptInvitationMutation = useAcceptAdvisorRvInvitation();
    const declineInvitationMutation = useDeclineAdvisorRvInvitation();
    const distributorsAndAdvisorsQuery = useDistributorsAndAdvisors();

    const pendingInvitationsCount = pendingInvitationsQuery.data?.count ?? 0;
    const firstPendingInvitation = pendingInvitationsQuery.data?.results?.[0];

    const advisorDistributorNames = useMemo(() => {
        const map: Record<number, string> = {};
        const results = distributorsAndAdvisorsQuery.data?.results ?? [];
        for (const el of results) {
            const distributorName = el.userNode.name;
            for (const child of el.children) {
                const existing = map[child.id];
                map[child.id] = existing ? `${existing}, ${distributorName}` : distributorName;
            }
        }
        return map;
    }, [distributorsAndAdvisorsQuery.data?.results]);

    const alertTitle = useMemo(() => {
        if (pendingInvitationsCount === 1 && firstPendingInvitation) {
            const advisorName = firstPendingInvitation.advisor.name;
            const distributorName = advisorDistributorNames[firstPendingInvitation.advisor.id] ?? '';
            const parentPart = distributorName ? ` (${distributorName})` : '';
            return _('{{advisorName}}{{parentPart}} te envió una solicitud de vinculación.', {
                advisorName,
                parentPart,
            });
        }
        if (pendingInvitationsCount > 1) {
            return _('Tenés {{count}} solicitudes de vinculación pendientes de revisión.', {
                count: pendingInvitationsCount,
            });
        }

        return _('Gestioná tus solicitudes de vinculación.');
    }, [pendingInvitationsCount, firstPendingInvitation, advisorDistributorNames]);

    const handleAcceptSingleInvitation = useCallback(() => {
        if (firstPendingInvitation) {
            acceptInvitationMutation.trigger(firstPendingInvitation.id);
        }
    }, [firstPendingInvitation, acceptInvitationMutation]);

    const handleDeclineSingleInvitation = useCallback(() => {
        if (firstPendingInvitation) {
            declineInvitationMutation.trigger(firstPendingInvitation.id);
        }
    }, [firstPendingInvitation, declineInvitationMutation]);

    const alertButtons = useMemo<AlertAction[]>(() => {
        if (pendingInvitationsCount === 1) {
            return [
                {
                    key: 'decline',
                    kind: 'secondary',
                    label: _('Rechazar'),
                    onClick: handleDeclineSingleInvitation,
                    loading: declineInvitationMutation.isMutating,
                },
                {
                    key: 'accept',
                    kind: 'primary',
                    label: _('Aceptar'),
                    onClick: handleAcceptSingleInvitation,
                    loading: acceptInvitationMutation.isMutating,
                },
            ];
        }
        if (pendingInvitationsCount > 1) {
            return [
                {
                    key: 'view',
                    kind: 'link',
                    label: _('Ver solicitudes'),
                    onClick: () => {
                        setStep('pending-invites');
                        setOpen(true);
                    },
                },
            ];
        }
        return [
            {
                key: 'view',
                kind: 'secondary',
                label: _('Ver solicitudes'),
                onClick: () => {
                    setStep('pending-invites');
                    setOpen(true);
                },
            },
            {
                key: 'send',
                kind: 'primary',
                label: _('Enviar nueva solicitud'),
                onClick: () => {
                    setStep('advisor-selector');
                    setOpen(true);
                },
            },
        ];
    }, [
        pendingInvitationsCount,
        acceptInvitationMutation.isMutating,
        declineInvitationMutation.isMutating,
        handleAcceptSingleInvitation,
        handleDeclineSingleInvitation,
    ]);

    if (advisorRvLinksQuery.isLoading || advisorsRvInvitationsQuery.isLoading) return null;

    return (
        <>
            {/* Alert  */}
            <Alert severity='blue' sx={{ alignItems: 'center', my: 1 }} actions={alertButtons}>
                <Typography>{alertTitle}</Typography>
            </Alert>

            {/* Modal */}
            {/* Main Flow */}
            {step === 'initial' && !hasLinkedOrInvitedAdvisors && (
                <RVOnboardingModalInitial
                    isRV
                    open={open}
                    onClose={() => setOpen(false)}
                    onNext={() => {
                        if ((pendingInvitationsQuery?.data?.count || 0) > 0) {
                            setStep('new-invitations-received');
                        }
                        setStep('advisor-selector');
                    }}
                />
            )}
            {step === 'new-invitations-received' && (
                <RVOnboardingModalNewInvitationsReceived
                    pendingInvitationsQuery={pendingInvitationsQuery}
                    open={open}
                    onClose={() => setOpen(false)}
                    onInviteMoreAdvisors={() => setStep('advisor-selector')}
                />
            )}
            {step === 'advisor-selector' && <RVOnboardingModalAdvisorSelector open={open} setOpen={setOpen} />}

            {/* Secondary Flow */}
            {step === 'pending-invites' && (
                <RVOnboardingModalInvites
                    open={open}
                    onClose={() => setOpen(false)}
                    onInviteMoreAdvisors={() => setStep('advisor-selector')}
                />
            )}
        </>
    );
};
export default RVOnboardingModalAndAlert;
